"use client";

import { useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/components/common/Link";
const eras = ["Residential", "Commercial", "Industrial", "Retail", "Hospitality"];
const properties = [{
  id: 1,
  era: "Residential · 2023",
  price: "Premium",
  name: "Luxury Villa Climate Control",
  address: "Sussex Coastal Estate",
  specs: "MULTI-ZONE / SMART / ENERGY-EFFICIENT / WHISPER-QUIET",
  badge: "NEW PROJECT",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/residential_climate_control.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/residential_climate_control.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/residential_climate_control.png-wvc-sizes",
  notes: "A bespoke climate control solution for a high-end coastal residence. We integrated multi-zone VRF systems to ensure perfect comfort in every room while maintaining energy efficiency."
}, {
  id: 2,
  era: "Commercial · 2022",
  price: "Enterprise",
  name: "Office Complex HVAC",
  address: "Surrey Business Park",
  specs: "VRF / BMS INTEGRATION / HIGH-CAPACITY / SUSTAINABLE",
  badge: "COMPLETED",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_office_hvac.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_office_hvac.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_office_hvac.png-wvc-sizes",
  notes: "Full-scale HVAC installation for a modern office complex. Our team implemented advanced Building Management System (BMS) integration for optimal climate regulation."
}, {
  id: 3,
  era: "Industrial · 2023",
  price: "Custom",
  name: "Warehouse Cooling System",
  address: "Sussex Logistics Hub",
  specs: "HEAVY-DUTY / PRECISION / VENTILATION / RELIABLE",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/industrial_warehouse_cooling.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/industrial_warehouse_cooling.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/industrial_warehouse_cooling.png-wvc-sizes",
  notes: "Engineered a robust cooling and ventilation system for a large-scale logistics facility, ensuring temperature stability for sensitive inventory."
}, {
  id: 4,
  era: "Retail · 2024",
  price: "Standard",
  name: "Boutique Store Climate",
  address: "Surrey High Street",
  specs: "COMPACT / SILENT / AESTHETIC / EFFICIENT",
  badge: "NEW PROJECT",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/retail_boutique_climate.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/retail_boutique_climate.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/retail_boutique_climate.webp-wvc-sizes",
  notes: "Designed a discreet, high-performance climate system for a luxury retail space, focusing on silent operation and seamless architectural integration."
}, {
  id: 5,
  era: "Hospitality · 2023",
  price: "Premium",
  name: "Hotel Suite Comfort",
  address: "Sussex Boutique Hotel",
  specs: "INDIVIDUAL / SMART / WHISPER-QUIET / LUXURY",
  badge: "COMPLETED",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hospitality_hotel_comfort.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hospitality_hotel_comfort.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hospitality_hotel_comfort.png-wvc-sizes",
  notes: "Upgraded the climate control systems for a boutique hotel, providing guests with individual room control and whisper-quiet performance."
}, {
  id: 6,
  era: "Commercial · 2024",
  price: "Enterprise",
  name: "Data Center Cooling",
  address: "Surrey Tech Campus",
  specs: "PRECISION / REDUNDANT / HIGH-PERFORMANCE / SECURE",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_data_center.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_data_center.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/commercial_data_center.webp-wvc-sizes",
  notes: "Installed mission-critical precision cooling systems for a high-density data center, ensuring 24/7 uptime and thermal stability."
}];
const carouselImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_installation_process.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_installation_process.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_installation_process.webp-wvc-sizes",
  alt: "HVAC installation process"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_system_components.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_system_components.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_system_components.webp-wvc-sizes",
  alt: "HVAC system components"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_finished_installation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_finished_installation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/hvac_finished_installation.png-wvc-sizes",
  alt: "Finished HVAC installation"
}];
export default function Projectcasestudies() {
  const [selectedEras, setSelectedEras] = useState<string[]>([]);
  const [priceMax, setPriceMax] = useState(5000);
  const toggleEra = (era: string) => {
    setSelectedEras(prev => prev.includes(era) ? prev.filter(e => e !== era) : [...prev, era]);
  };
  const clearAll = () => {
    setSelectedEras([]);
    setPriceMax(5000);
  };
  return <section data-section-id="3335" id="projectcasestudies" className="bg-background text-foreground font-default">
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 md:col-start-2">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6 md:mb-8">
              N° 01 — Our Projects
            </p>
            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              The <em className="italic font-light">Coastline</em>
              <br />
              portfolio.
            </h1>
          </div>

          <div className="col-span-12 md:col-span-3 md:col-start-10 flex flex-col gap-4 md:items-end md:text-right justify-end">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Showing 6 of 6 Projects
            </p>
            <div className="flex items-center gap-2 md:justify-end">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Sort by:
              </span>
              <Select defaultValue="recent">
                <SelectTrigger size="sm" className="font-mono text-xs uppercase tracking-[0.1em] border-0 border-b border-border bg-transparent px-0 h-auto py-1 focus-visible:ring-0 hover:bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="font-mono text-xs">
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="type">By Sector</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-y border-border">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-center lg:h-[72px] py-4 lg:py-0 gap-4 lg:gap-0 overflow-x-auto">
            <div className="flex items-center gap-3 lg:pr-6 lg:border-r lg:border-border lg:h-full">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0">
                Sector
              </span>
              <div className="flex gap-2 flex-wrap">
                {eras.map((era, i) => <Button key={era} data-index={i} onClick={() => toggleEra(era)} variant="outline" size="sm" className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-md border h-7 px-3 transition-colors ${selectedEras.includes(era) ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "border-border text-foreground hover:border-primary/40 hover:bg-transparent"}`}>
                    {era}
                  </Button>)}
              </div>
            </div>

            <div className="lg:ml-auto lg:pl-6">
              <Button variant="ghost" size="sm" onClick={clearAll} className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-primary hover:bg-transparent px-0 h-auto py-1">
                Clear all
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {properties.map((property, i) => <article key={property.id} data-index={i} className="group flex flex-col">
              <div className="relative overflow-hidden aspect-[4/5] bg-muted rounded-lg shadow-md">
                <div className="absolute top-0 left-0 right-0 h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                <img src={property.image} data-wvc-srcset={property.srcset} data-wvc-sizes={property.sizes} alt={property.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                {property.badge && <Badge className={`absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.15em] rounded-md px-2.5 py-1 border-0 ${property.badgeType === "new" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                    {property.badge}
                  </Badge>}
              </div>

              <div className="flex items-baseline justify-between mt-6 gap-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {property.era}
                </span>
                <span className="font-default text-2xl text-foreground">
                  {property.price}
                </span>
              </div>

              <h3 className="font-default text-[22px] leading-tight mt-3 text-foreground">
                {property.name}
              </h3>

              <p className="text-sm text-muted-foreground mt-2 font-default">
                {property.address}
              </p>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-foreground/80">
                  {property.specs}
                </p>
              </div>

              <div className="flex items-center justify-between mt-5">
                <Link to="/AirConditioningPage" className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary border-b border-primary/30 pb-0.5 hover:border-primary transition-colors">
                  View Details →
                </Link>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-primary hover:bg-transparent px-0 h-auto py-1 gap-1.5">
                      Quick View
                      <Plus className="size-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent showCloseButton={false} className="max-w-5xl w-[calc(100%-2rem)] p-0 bg-background border-border rounded-lg overflow-hidden gap-0">
                    <div className="grid grid-cols-1 md:grid-cols-5 max-h-[85vh]">
                      <div className="md:col-span-3 bg-muted relative">
                        <Carousel className="h-full">
                          <CarouselContent className="ml-0 h-full">
                            {carouselImages.map((img, idx) => <CarouselItem key={idx} data-index={idx} className="pl-0">
                                <div className="aspect-[4/5] md:aspect-auto md:h-[85vh] w-full">
                                  <img src={img.src} data-wvc-srcset={img.srcset} data-wvc-sizes={img.sizes} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover max-w-full" />
                                </div>
                              </CarouselItem>)}
                          </CarouselContent>
                          <CarouselPrevious className="left-4 bg-background/90 border-border rounded-md size-9" />
                          <CarouselNext className="right-4 bg-background/90 border-border rounded-md size-9" />
                        </Carousel>
                      </div>

                      <div className="md:col-span-2 relative overflow-y-auto max-h-[85vh]">
                        <div className="absolute top-4 right-4 z-10">
                          <DialogClose asChild>
                            <Button variant="ghost" size="icon" className="size-8 rounded-md hover:bg-muted">
                              <X className="size-4" />
                              <span className="sr-only">Close</span>
                            </Button>
                          </DialogClose>
                        </div>
                        <div className="p-8 md:p-10">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                            {property.era}
                          </p>
                          <h2 className="font-default text-3xl md:text-4xl leading-tight text-foreground">
                            {property.name}
                          </h2>
                          <p className="text-sm text-muted-foreground mt-2 font-default">
                            {property.address}
                          </p>
                          <p className="font-default text-3xl text-foreground mt-6">
                            {property.price}
                          </p>

                          <div className="mt-8 pt-6 border-t border-border grid grid-cols-2 gap-y-4 gap-x-6">
                            {[{
                          label: "System Type",
                          value: "VRF"
                        }, {
                          label: "Efficiency",
                          value: "A++"
                        }, {
                          label: "Installation",
                          value: "2024"
                        }, {
                          label: "Location",
                          value: "Sussex"
                        }].map((spec, idx) => <div key={spec.label} data-index={idx}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                  {spec.label}
                                </p>
                                <p className="font-default text-lg text-foreground mt-1">
                                  {spec.value}
                                </p>
                              </div>)}
                          </div>

                          <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                              Project Notes
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/80 font-default">
                              {property.notes}
                            </p>
                          </div>

                          <div className="mt-8 flex flex-col gap-3">
                            <Button className="w-full rounded-md h-12 font-mono text-[11px] uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:bg-primary/90">
                              Request Consultation
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </article>)}
        </div>

        <div className="mt-20 md:mt-24 flex justify-center">
          <Pagination>
            
          </Pagination>
        </div>
      </div>
    </section>;
}